import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import SEO from '@/components/university/common/SEO'
import UniversityHeader from '@/components/university/layout/UniversityHeader';
import UniversityFooter from '@/components/university/layout/UniversityFooter';

const M = '#06060c'
const D = '#0a0a14'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'
const SUCCESS = '#4ade80'

const ORG_TYPES = [
  { value: 'charity', label: '🏛️ Registered charity' },
  { value: 'ngo', label: '🌍 Non-governmental organisation (NGO)' },
  { value: 'social_enterprise', label: '🤝 Social enterprise' },
  { value: 'government', label: '🏢 Government / public service' },
  { value: 'community', label: '👥 Community group' },
  { value: 'other', label: '📋 Other not-for-profit' },
]

const CAUSE_AREAS = [
  { value: 'domestic_abuse', label: '🏠 Domestic abuse' },
  { value: 'refugee', label: '🌍 Refugees & asylum seekers' },
  { value: 'youth', label: '🌱 At-risk youth' },
  { value: 'prison', label: '🔓 Prison & rehabilitation' },
  { value: 'homelessness', label: '🤝 Homelessness' },
  { value: 'disability', label: '♿ Disability support' },
  { value: 'mental_health', label: '🧠 Mental health' },
  { value: 'general', label: '💙 General welfare' },
]

const PRICING_TIERS = [
  {
    name: 'NFP Per Seat',
    price: '$9',
    period: '/seat/month',
    desc: 'Pay only for active members. Perfect for organisations with variable cohort sizes.',
    features: ['Pay per active member', 'Full degree access per seat', 'Admin dashboard', 'Progress tracking', 'Grade privacy for members'],
    highlight: false,
  },
  {
    name: 'NFP Cohort',
    price: '$99',
    period: '/month flat',
    desc: 'Up to 20 members. Flat monthly fee — no per-seat counting.',
    features: ['Up to 20 members', 'Flat predictable billing', 'Full degree access', 'Admin dashboard', 'Progress tracking', 'Grade privacy'],
    highlight: true,
    badge: 'MOST POPULAR',
  },
]

export default function NFPPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [isMobile, setIsMobile] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [orgName, setOrgName] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [country, setCountry] = useState('AU')
  const [charityNumber, setCharityNumber] = useState('')
  const [orgType, setOrgType] = useState('charity')
  const [causeArea, setCauseArea] = useState('domestic_abuse')
  const [appMessage, setAppMessage] = useState('')

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  async function handleSubmit() {
    if (!orgName.trim() || !contactName.trim() || !contactEmail.trim() || !appMessage.trim()) {
      setError(t('university.nfp.errorRequired', 'Please fill in all required fields.'))
      return
    }

    setLoading(true); setError(null)

    try {
      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

      const res = await fetch(`${supabaseUrl}/functions/v1/scholarship-fund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'nfp_apply',
          name: orgName.trim(),
          contact_name: contactName.trim(),
          contact_email: contactEmail.trim(),
          contact_phone: contactPhone.trim() || undefined,
          website: website.trim() || undefined,
          country,
          charity_number: charityNumber.trim() || undefined,
          organisation_type: orgType,
          cause_area: causeArea,
          application_message: appMessage.trim(),
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Application failed')
      setSubmitted(true)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          style={{ maxWidth: 520, width: '100%', margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🙌</div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>
            {t('university.nfp.successTitle', 'Application Received')}
          </h1>
          <p style={{ color: MU, fontSize: 15, lineHeight: 1.7, margin: '0 0 32px' }}>
            {t('university.nfp.successText', 'Thank you for what you do. We review NFP applications within 5 business days and will contact you at the email provided to discuss your organisation\'s needs.')}
          </p>
          <button onClick={() => navigate('/university/dashboard')} style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '14px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            {t('university.nfp.successBtn', 'Back to Home')}
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Not-For-Profit (NFP) Programme"
        description="Subsidized university education for registered charities, NGOs, and social enterprises. Empower the communities you serve with world-class AI learning."
      />

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg, #07071a 0%, ${D} 100%)`, borderBottom: `1px solid ${BR}` }}>
        <div style={{ maxWidth: 780, margin: '0 auto', padding: isMobile ? '40px 20px 36px' : '64px 24px 52px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🏛️</div>
            <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2.5px', marginBottom: 12 }}>
              {t('university.nfp.eyebrow', 'NOT-FOR-PROFIT PROGRAMME')}
            </p>
            <h1 style={{ fontSize: isMobile ? 26 : 42, fontWeight: 900, margin: '0 0 16px', letterSpacing: '-1px', fontFamily: "'Playfair Display', Georgia, serif" }}>
              {t('university.nfp.title', 'Education for the Organisations That Need It Most')}
            </h1>
            <p style={{ color: MU, fontSize: isMobile ? 14 : 16, lineHeight: 1.75, maxWidth: 580, margin: '0 auto 32px' }}>
              {t('university.nfp.subtitle', 'Still Waters University offers deeply subsidised access for registered charities, NGOs, and social enterprises. A domestic abuse shelter. A refugee centre. A prison rehabilitation programme. If you serve people in need, we want to help you serve them better.')}
            </p>
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={() => { setShowForm(true); setTimeout(() => document.getElementById('nfp-form')?.scrollIntoView({ behavior: 'smooth' }), 50) }}
              style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '16px 36px', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}>
              {t('university.nfp.applyBtn', 'Apply for NFP Access →')}
            </motion.button>
          </motion.div>
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '56px 24px 80px' }}>

        {/* Who qualifies */}
        <div style={{ marginBottom: 56 }}>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2px', marginBottom: 24, textAlign: 'center' }}>
            {t('university.nfp.qualifiesTitle', 'WHO QUALIFIES')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 14 }}>
            {[
              { icon: '🏠', label: t('university.nfp.q1', 'Domestic abuse shelters & refuges') },
              { icon: '🌍', label: t('university.nfp.q2', 'Refugee & asylum seeker services') },
              { icon: '🌱', label: t('university.nfp.q3', 'Youth at-risk programmes') },
              { icon: '🔓', label: t('university.nfp.q4', 'Prison education & rehabilitation') },
              { icon: '🤝', label: t('university.nfp.q5', 'Homelessness & housing support') },
              { icon: '♿', label: t('university.nfp.q6', 'Disability support organisations') },
              { icon: '🧠', label: t('university.nfp.q7', 'Mental health charities') },
              { icon: '💙', label: t('university.nfp.q8', 'Any registered not-for-profit') },
            ].map((q, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 14, padding: '18px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 26, marginBottom: 8 }}>{q.icon}</div>
                <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.45 }}>{q.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div style={{ marginBottom: 56 }}>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2px', marginBottom: 8, textAlign: 'center' }}>
            {t('university.nfp.pricingTitle', 'NFP PRICING')}
          </p>
          <p style={{ fontSize: 13, color: MU, textAlign: 'center', marginBottom: 28 }}>
            {t('university.nfp.pricingNote', 'Standard corporate pricing is $39/seat/month. NFP pricing is subsidised by our Pay It Forward donors and commercial subscribers.')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 18, maxWidth: 680, margin: '0 auto' }}>
            {PRICING_TIERS.map(plan => (
              <div key={plan.name} style={{ background: plan.highlight ? 'rgba(212,168,67,0.06)' : CA, border: `${plan.highlight ? 2 : 1}px solid ${plan.highlight ? G : BR}`, borderRadius: 20, padding: '28px 24px', position: 'relative' }}>
                {plan.badge && (
                  <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: G, color: '#06060c', borderRadius: 100, padding: '3px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                    {plan.badge}
                  </div>
                )}
                <p style={{ fontWeight: 800, fontSize: 14, margin: '0 0 12px', color: plan.highlight ? G : TX }}>{plan.name}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
                  <span style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-1px', lineHeight: 1 }}>{plan.price}</span>
                  <span style={{ fontSize: 13, color: MU }}>{plan.period}</span>
                </div>
                <p style={{ fontSize: 13, color: MU, margin: '0 0 18px', lineHeight: 1.5 }}>{plan.desc}</p>
                <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <span style={{ color: G, fontSize: 11, fontWeight: 900, flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: 13, color: MU }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: MU, textAlign: 'center', marginTop: 16 }}>
            {t('university.nfp.pricingAdmin', 'Pricing is set by the Still Waters University team and may be adjusted based on your organisation\'s size and needs.')}
          </p>
        </div>

        {/* Application form */}
        <div id="nfp-form">
          <AnimatePresence>
            {!showForm ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center' }}>
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setShowForm(true)}
                  style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '18px 48px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}>
                  {t('university.nfp.applyBtn', 'Apply for NFP Access →')}
                </motion.button>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2px', marginBottom: 24, textAlign: 'center' }}>
                  {t('university.nfp.formTitle', 'NFP APPLICATION')}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 28 }}>
                  {/* Left column */}
                  <div>
                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '24px', marginBottom: 20 }}>
                      <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 18 }}>
                        {t('university.nfp.orgSection', 'ORGANISATION')}
                      </p>
                      {[
                        { label: t('university.nfp.orgName', 'Organisation Name *'), value: orgName, set: setOrgName, placeholder: 'Hope House Women\'s Shelter' },
                        { label: t('university.nfp.website', 'Website (optional)'), value: website, set: setWebsite, placeholder: 'https://yourorg.org.au' },
                        { label: t('university.nfp.charityNumber', 'Charity / Registration Number'), value: charityNumber, set: setCharityNumber, placeholder: 'ABN or charity number' },
                      ].map(f => (
                        <div key={f.label} style={{ marginBottom: 14 }}>
                          <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>{f.label}</label>
                          <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      ))}

                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>
                          {t('university.nfp.orgType', 'Organisation Type *')}
                        </label>
                        <select value={orgType} onChange={e => setOrgType(e.target.value)}
                          style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: '#0a0a14', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }}>
                          {ORG_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>

                      <div style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>
                          {t('university.nfp.causeArea', 'Primary Cause Area *')}
                        </label>
                        <select value={causeArea} onChange={e => setCauseArea(e.target.value)}
                          style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: '#0a0a14', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }}>
                          {CAUSE_AREAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Right column */}
                  <div>
                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '24px', marginBottom: 20 }}>
                      <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 18 }}>
                        {t('university.nfp.contactSection', 'CONTACT')}
                      </p>
                      {[
                        { label: t('university.nfp.contactName', 'Your Name *'), value: contactName, set: setContactName, placeholder: 'Jane Smith', type: 'text' },
                        { label: t('university.nfp.contactEmail', 'Email Address *'), value: contactEmail, set: setContactEmail, placeholder: 'jane@yourorg.org', type: 'email' },
                        { label: t('university.nfp.contactPhone', 'Phone (optional)'), value: contactPhone, set: setContactPhone, placeholder: '+61 400 000 000', type: 'tel' },
                        { label: t('university.nfp.country', 'Country'), value: country, set: setCountry, placeholder: 'AU', type: 'text' },
                      ].map(f => (
                        <div key={f.label} style={{ marginBottom: 14 }}>
                          <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>{f.label}</label>
                          <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} type={f.type}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>

                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '24px' }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>
                        {t('university.nfp.message', 'Tell us about your organisation and who you serve *')}
                      </label>
                      <textarea value={appMessage} onChange={e => setAppMessage(e.target.value)} rows={5}
                        placeholder={t('university.nfp.messagePlaceholder', "e.g. We're a domestic abuse shelter supporting 30-40 women at any time. Many of our residents want to study or retrain but have no income. We want to give them access to education as part of their recovery programme...")}
                        style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: '12px 16px', margin: '20px 0', fontSize: 13, color: '#f87171' }}>
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={handleSubmit} disabled={loading}
                  style={{ width: '100%', marginTop: 24, background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '18px', fontSize: 16, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}>
                  {loading ? t('university.nfp.submitting', 'Submitting…') : t('university.nfp.submitBtn', 'Submit NFP Application →')}
                </motion.button>
                <p style={{ fontSize: 12, color: MU, textAlign: 'center', marginTop: 12 }}>
                  {t('university.nfp.submitNote', 'We review applications within 5 business days · No payment required to apply')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom nudge to give */}
        <div style={{ marginTop: 56, background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 18, padding: isMobile ? '24px 20px' : '28px 32px', display: 'flex', alignItems: 'center', gap: 20, flexDirection: isMobile ? 'column' : 'row', textAlign: isMobile ? 'center' : 'left' }}>
          <div style={{ fontSize: 36, flexShrink: 0 }}>💙</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 6px' }}>
              {t('university.nfp.supportFund', 'Help fund the NFP programme')}
            </p>
            <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.55 }}>
              {t('university.nfp.supportFundText', 'The NFP programme is partly funded by our Pay It Forward donors. If you\'re not an NFP but want to help, every contribution goes directly to funding someone\'s education.')}
            </p>
          </div>
          <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate('/university/give')}
            style={{ background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: 12, padding: '12px 22px', color: '#7dd3fc', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {t('university.nfp.giveBtn', 'Pay It Forward →')}
          </motion.button>
        </div>
      </div>
    </div>
  )
}
